import React, { useEffect, useRef, useState } from 'react';

export const ACHIEVEMENT_STUDIO_PATH = '/achievement-studio';
export const ARCHIVE_API_PATH = '/achievement-studio/api/archive';
export const REORDER_API_PATH = '/achievement-studio/api/archive/reorder';
export const FROM_IMAGE_API_PATH = '/achievement-studio/api/archive/from-image';

export const ROW_LIMITS = { vertical: 3, square: 2, horizontal: 1 };

const RENDERER_SCRIPT_ID = 'achievement-renderer-script';
const RENDERER_STYLE_ID = 'achievement-renderer-style';
const RENDERER_VERSION = '23';
const RENDERER_SCRIPT_SRC = `/achievement-studio/static/achievement-renderer.js?v=${RENDERER_VERSION}`;
const RENDERER_STYLE_HREF = `/achievement-studio/static/achievement-renderer.css?v=${RENDERER_VERSION}`;

let rendererLoadPromise = null;

export function ensureRendererAssets() {
  if (typeof document === 'undefined') {
    return Promise.resolve(null);
  }

  if (!document.getElementById(RENDERER_STYLE_ID)) {
    const link = document.createElement('link');
    link.id = RENDERER_STYLE_ID;
    link.rel = 'stylesheet';
    link.href = RENDERER_STYLE_HREF;
    document.head.appendChild(link);
  }

  if (window.AchievementRenderer) {
    return Promise.resolve(window.AchievementRenderer);
  }

  if (rendererLoadPromise) {
    return rendererLoadPromise;
  }

  rendererLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(RENDERER_SCRIPT_ID);
    const script = existingScript || document.createElement('script');

    script.addEventListener('load', () => resolve(window.AchievementRenderer));
    script.addEventListener('error', () => reject(new Error('Achievement renderer unavailable')));

    if (!existingScript) {
      script.id = RENDERER_SCRIPT_ID;
      script.src = RENDERER_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  });

  return rendererLoadPromise;
}

export function achievementTitle(record) {
  return record?.title || record?.achievement?.name || 'Untitled card';
}

export function achievementAsset(record) {
  return record?.asset || record?.achievement?.asset || 'FORECAST';
}

export function rendererStageClass(record, displayMode = 'tile') {
  const visual = record?.achievement?.visual || {};
  const shape = visual.cardShape || 'horizontal';
  return `achievement-render-stage achievement-page-card is-${displayMode} shape-${shape}`;
}

export function AchievementRendererCard({ record, displayMode = 'tile' }) {
  const stageRef = useRef(null);
  const [isReady, setIsReady] = useState(() => typeof window !== 'undefined' && Boolean(window.AchievementRenderer));
  const [rendererError, setRendererError] = useState('');

  useEffect(() => {
    let isMounted = true;

    ensureRendererAssets()
      .then(() => {
        if (isMounted) {
          setIsReady(Boolean(window.AchievementRenderer));
          setRendererError('');
        }
      })
      .catch(() => {
        if (isMounted) {
          setRendererError('Preview unavailable.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isReady || !stageRef.current || !window.AchievementRenderer) {
      return;
    }

    window.AchievementRenderer.renderInto(stageRef.current, record?.achievement || {}, {
      mode: 'view',
      stageClassName: rendererStageClass(record, displayMode),
    });
  }, [displayMode, isReady, record]);

  // ponytail: renderer sizes text/shapes in absolute px, so the card only scales
  // proportionally if the whole thing is zoomed. Design widths mirror achievement-renderer.css.
  useEffect(() => {
    const stage = stageRef.current;
    if (displayMode !== 'tile' || !stage || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const visual = record?.achievement?.visual || {};
    const shape = visual.cardShape || 'horizontal';
    const customWidth = Number(visual.cardWidth);
    const designWidth = customWidth > 0 ? customWidth : { vertical: 420, square: 560 }[shape] || 820;

    // ponytail: write on the next frame and skip no-ops — mutating inside the
    // callback re-triggers the observer ("ResizeObserver loop" warning).
    let frame = 0;
    let applied = '';

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      const zoom = Math.min(1, width / designWidth).toFixed(4);
      if (zoom === applied) {
        return;
      }
      applied = zoom;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => stage.style.setProperty('--card-zoom', zoom));
    });
    observer.observe(stage);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [displayMode, isReady, record]);

  return (
    <div
      ref={stageRef}
      className={rendererStageClass(record, displayMode)}
      data-achievement-renderer="shared"
    >
      {!isReady && !rendererError && (
        <div className="grid min-h-[180px] w-full place-items-center rounded-lg border border-olive-100 bg-olive-50 text-sm font-bold text-olive-500">
          Loading preview
        </div>
      )}
      {rendererError && (
        <div className="grid min-h-[180px] w-full place-items-center rounded-lg border border-amber-200 bg-amber-50 text-sm font-bold text-amber-800">
          {rendererError}
        </div>
      )}
    </div>
  );
}

export function publicCardShape(record) {
  const shape = record?.achievement?.visual?.cardShape || 'horizontal';
  return ['vertical', 'square'].includes(shape) ? shape : 'horizontal';
}

export function publicShapeClass(record) {
  return `public-shape-${publicCardShape(record)}`;
}

export function buildPublicRows(items) {
  const rows = [];
  let activeRow = null;

  items.forEach((item) => {
    const shape = publicCardShape(item);

    if (
      shape === 'horizontal'
      || !activeRow
      || activeRow.shape !== shape
      || activeRow.items.length >= ROW_LIMITS[shape]
    ) {
      activeRow = { shape, items: [] };
      rows.push(activeRow);
    }

    activeRow.items.push(item);

    if (shape === 'horizontal') {
      activeRow = null;
    }
  });

  return rows;
}
